import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CategorySelectorComponent } from '../../shared/design-system/category-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { FormFieldErrorsState } from '../../shared/design-system/form-field-errors';
import { CatalogOffering, CatalogService, CatalogWarehouse, CreateOfferingPayload, InventoryLedgerEntry, InventoryStockBalance } from './catalog.service';
import { CatalogOfferingMediaManagerComponent } from './catalog-offering-media-manager.component';
import { CatalogOfferingAttributesComponent } from './catalog-offering-attributes.component';
import { OfferingProfileFormComponent } from './offering-profile-form.component';
import { offeringProfileTypeOptions } from './offering-profile-type-options';
import { defaultProfileModel, serializeProfilePayload } from './offering-profile-schemas';
import { OfferingInventoryWorkspaceComponent, OfferingInventorySubTab } from './offering-inventory-workspace.component';
import { OfferingStorefrontPublishingPanelComponent } from './offering-storefront-publishing-panel.component';

type OfferingTab = 'general' | 'media' | 'profiles' | 'attributes' | 'flags' | 'inventory' | 'storefront';
type OfferingGeneralSection = 'core' | 'seo' | 'pricing';

@Component({
  standalone: true,
  selector: 'ain-catalog-offering-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    RadioButtonModule,
    CheckboxModule,
    EditorModule,
    TableModule,
    TagModule,
    CurrencySelectorComponent,
    CategorySelectorComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    OfferingProfileFormComponent,
    CatalogOfferingMediaManagerComponent,
    CatalogOfferingAttributesComponent,
    OfferingInventoryWorkspaceComponent,
    OfferingStorefrontPublishingPanelComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('catalog.offerings.createTitle') : t('catalog.offerings.detailTitle')" [description]="t('catalog.offerings.detailDescription')">
      <ain-info-hint page-title-addon [titleKey]="'catalog.offerings.hint.title'" [descriptionKey]="'catalog.offerings.hint.description'" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.offerings.newRecord') : form.title || t('catalog.offerings.newRecord') }}</h3>
            <p>
              @if (isCreateMode()) {
                {{ t('catalog.offerings.createHint') }}
              } @else {
                {{ form.code }} • {{ form.type }} • {{ form.status }}
                @if (offering()?.isCodingLocked) {
                  • <p-tag severity="warn" [value]="t('catalog.offerings.codingLocked')" />
                }
              }
            </p>
          </div>
          <div class="actions">
            <a [routerLink]="['/catalog/products']" [queryParams]="{ tab: 'offerings' }" class="back-link">{{ t('catalog.action.backToList') }}</a>
            @if (!isCreateMode()) {
              <button pButton type="button" [label]="t('catalog.action.publish')" [disabled]="form.status === 'Published'" (click)="publish()"></button>
              <button pButton type="button" severity="secondary" [label]="t('catalog.action.unpublish')" [disabled]="form.status !== 'Published'" (click)="unpublish()"></button>
            }
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </div>
        </section>

        <section class="tabs">
          <button type="button" [class.active]="activeTab() === 'general'" (click)="setTab('general')">{{ t('catalog.tabs.general') }}</button>
          <button type="button" [class.active]="activeTab() === 'media'" [disabled]="isCreateMode()" (click)="setTab('media')">{{ t('catalog.tabs.media') }}</button>
          <button type="button" [class.active]="activeTab() === 'profiles'" [disabled]="isCreateMode()" (click)="setTab('profiles')">{{ t('catalog.tabs.profiles') }}</button>
          <button type="button" [class.active]="activeTab() === 'attributes'" [disabled]="isCreateMode()" (click)="setTab('attributes')">{{ t('catalog.tabs.attributes') }}</button>
          <button type="button" [class.active]="activeTab() === 'flags'" (click)="setTab('flags')">{{ t('catalog.tabs.flags') }}</button>
          @if (canViewInventoryTab()) {
            <button type="button" [class.active]="activeTab() === 'inventory'" [disabled]="isCreateMode()" (click)="setTab('inventory')">{{ t('catalog.tabs.inventory') }}</button>
          }
          @if (canViewStorefrontTab()) {
            <button type="button" [class.active]="activeTab() === 'storefront'" [disabled]="isCreateMode()" (click)="setTab('storefront')">{{ t('catalog.tabs.storefront') }}</button>
          }
        </section>

        @if (activeTab() === 'general') {
          <section class="general-subtabs">
            <button type="button" [class.active]="generalSection() === 'core'" (click)="generalSection.set('core')">{{ t('catalog.offerings.sections.core') }}</button>
            <button type="button" [class.active]="generalSection() === 'seo'" (click)="generalSection.set('seo')">{{ t('catalog.offerings.sections.seo') }}</button>
            <button type="button" [class.active]="generalSection() === 'pricing'" (click)="generalSection.set('pricing')">{{ t('catalog.offerings.sections.pricing') }}</button>
          </section>

          @if (generalSection() === 'core') {
            <section class="section-card">
              <div class="section-header">
                <h4>{{ t('catalog.offerings.sections.core') }}</h4>
                <ain-info-hint
                  [titleKey]="'catalog.offerings.sections.coreHint.title'"
                  [descriptionKey]="'catalog.offerings.sections.coreHint.description'"
                  [bulletKeys]="[
                    'catalog.offerings.sections.coreHint.bullets.codeTitle',
                    'catalog.offerings.sections.coreHint.bullets.partialSave',
                    'catalog.offerings.sections.coreHint.bullets.finance'
                  ]" />
              </div>
              <div class="form-grid">
                <label>
                  <span>{{ t('columns.type') }}</span>
                  <p-select [(ngModel)]="form.type" [options]="typeOptions" optionLabel="label" optionValue="value" appendTo="body" />
                </label>
                <ain-form-field [label]="t('columns.code')" [errorKey]="fieldErrors.errorKey('code')">
                  <input
                    pInputText
                    [(ngModel)]="form.code"
                    (ngModelChange)="fieldErrors.clear('code')"
                    [disabled]="!!offering()?.isCodingLocked"
                    [class.p-invalid]="fieldErrors.has('code')" />
                </ain-form-field>
                <ain-form-field [label]="t('columns.title')" [errorKey]="fieldErrors.errorKey('title')">
                  <input
                    pInputText
                    [(ngModel)]="form.title"
                    (ngModelChange)="fieldErrors.clear('title')"
                    [class.p-invalid]="fieldErrors.has('title')" />
                </ain-form-field>
                <label>
                  <span>{{ t('catalog.categories.title') }}</span>
                  <ain-category-selector [(ngModel)]="form.categoryId" [disabled]="!!offering()?.isCodingLocked" />
                </label>
                <label>
                  <span>{{ t('catalog.categories.consumptionClassification') }}</span>
                  <p-select
                    [(ngModel)]="form.consumptionClassification"
                    [options]="consumptionClassificationOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [disabled]="!!offering()?.isCodingLocked"
                    appendTo="body" />
                </label>
                <label>
                  <span>{{ t('columns.status') }}</span>
                  <p-select [(ngModel)]="form.status" [options]="statusOptions" optionLabel="label" optionValue="value" appendTo="body" />
                </label>
                <div class="radio-wrapper">
                  <span>{{ t('columns.visibility') }}</span>
                  <div class="radio-group">
                    <label class="radio-item">
                      <p-radiobutton name="offeringVisibility" inputId="visibility-public" value="Public" [(ngModel)]="form.visibility" />
                      <span>Public</span>
                    </label>
                    <label class="radio-item">
                      <p-radiobutton name="offeringVisibility" inputId="visibility-private" value="Private" [(ngModel)]="form.visibility" />
                      <span>Private</span>
                    </label>
                    <label class="radio-item">
                      <p-radiobutton name="offeringVisibility" inputId="visibility-unlisted" value="Unlisted" [(ngModel)]="form.visibility" />
                      <span>Unlisted</span>
                    </label>
                  </div>
                </div>
                <label class="full"><span>{{ t('catalog.offerings.shortDescription') }}</span><textarea pInputTextarea rows="3" [(ngModel)]="form.shortDescription"></textarea></label>
                <div class="full editor-wrapper">
                  <span>{{ t('catalog.offerings.fullDescription') }}</span>
                  <p-editor [(ngModel)]="form.fullDescription" [style]="{ height: '220px' }"></p-editor>
                </div>
              </div>
            </section>
          }

          @if (generalSection() === 'seo') {
            <section class="section-card">
              <div class="section-header">
                <h4>{{ t('catalog.offerings.sections.seo') }}</h4>
                <ain-info-hint
                  [titleKey]="'catalog.offerings.sections.seoHint.title'"
                  [descriptionKey]="'catalog.offerings.sections.seoHint.description'"
                  [bulletKeys]="[
                    'catalog.offerings.sections.seoHint.bullets.slug',
                    'catalog.offerings.sections.seoHint.bullets.meta',
                    'catalog.offerings.sections.seoHint.bullets.optional'
                  ]" />
              </div>
              <div class="form-grid">
                <label class="full"><span>Slug</span><input pInputText [(ngModel)]="form.slug" [placeholder]="t('catalog.offerings.sections.slugPlaceholder')" /></label>
                <label class="full"><span>{{ t('catalog.offerings.metaTitle') }}</span><input pInputText [(ngModel)]="form.metaTitle" /></label>
                <label class="full"><span>{{ t('catalog.offerings.metaDescription') }}</span><textarea pInputTextarea rows="4" [(ngModel)]="form.metaDescription"></textarea></label>
              </div>
            </section>
          }

          @if (generalSection() === 'pricing') {
            <section class="section-card">
              <div class="section-header">
                <h4>{{ t('catalog.offerings.sections.pricing') }}</h4>
                <ain-info-hint
                  [titleKey]="'catalog.offerings.sections.pricingHint.title'"
                  [descriptionKey]="'catalog.offerings.sections.pricingHint.description'"
                  [bulletKeys]="[
                    'catalog.offerings.sections.pricingHint.bullets.basePrice',
                    'catalog.offerings.sections.pricingHint.bullets.models',
                    'catalog.offerings.sections.pricingHint.bullets.optional'
                  ]" />
              </div>
              <div class="form-grid">
                <label><span>{{ t('columns.basePrice') }}</span><input pInputText type="number" min="0" [(ngModel)]="form.basePrice" /></label>
                <label>
                  <span>{{ t('columns.currency') }}</span>
                  <ain-currency-selector [(ngModel)]="form.currency" [showSearch]="true"></ain-currency-selector>
                </label>
                <label>
                  <span>{{ t('catalog.offerings.pricingModel') }}</span>
                  <p-select [(ngModel)]="form.pricingModel" [options]="pricingModelOptions" optionLabel="label" optionValue="value" appendTo="body" />
                </label>
                <label>
                  <span>{{ t('catalog.offerings.fulfillmentModel') }}</span>
                  <p-select [(ngModel)]="form.fulfillmentModel" [options]="fulfillmentModelOptions" optionLabel="label" optionValue="value" appendTo="body" />
                </label>
              </div>
            </section>
          }
        }

        @if (activeTab() === 'media') {
          @if (offering(); as currentOffering) {
            <ain-catalog-offering-media-manager
              [offeringId]="currentOffering.id"
              (changed)="onMediaChanged($event)" />
          }
        }

        @if (activeTab() === 'profiles') {
          <section class="form-grid">
            <label>
              <span>{{ t('catalog.profiles.profileType') }}</span>
              <p-select
                [(ngModel)]="selectedProfileType"
                [options]="profileTypeOptions()"
                optionLabel="label"
                optionValue="value"
                appendTo="body"
                (ngModelChange)="onProfileTypeChange($event)" />
            </label>
            <div class="profile-actions">
              <button pButton type="button" severity="secondary" [label]="t('catalog.profiles.load')" (click)="loadProfile()"></button>
              <button pButton type="button" severity="secondary" [label]="t('catalog.profiles.validate')" (click)="validateProfile()"></button>
              <button pButton type="button" [label]="t('catalog.profiles.save')" (click)="saveProfile()"></button>
            </div>
            <div class="full">
              <ain-offering-profile-form
                [profileType]="selectedProfileType"
                [(payloadJson)]="profilePayloadJson" />
            </div>
            @if (profileValidationErrors().length > 0) {
              <div class="full profile-errors">
                @for (errorKey of profileValidationErrors(); track errorKey) {
                  <p>{{ errorKey }}</p>
                }
              </div>
            }
          </section>
        }

        @if (activeTab() === 'attributes') {
          @if (offering(); as currentOffering) {
            <ain-catalog-offering-attributes [offeringId]="currentOffering.id" />
          }
        }

        @if (activeTab() === 'flags') {
          <ain-info-hint [titleKey]="'catalog.offerings.restrictionsHint.title'" [descriptionKey]="'catalog.offerings.restrictionsHint.description'" />
          <section class="flags-form">
            <label><p-checkbox [binary]="true" [(ngModel)]="form.salesEnabled" /> {{ t('catalog.flags.salesEnabled') }}</label>
            <label><p-checkbox [binary]="true" [(ngModel)]="form.purchaseEnabled" /> {{ t('catalog.flags.purchaseEnabled') }}</label>
            <label><p-checkbox [binary]="true" [(ngModel)]="form.inventoryEnabled" /> {{ t('catalog.flags.inventoryEnabled') }}</label>
            <label><p-checkbox [binary]="true" [(ngModel)]="form.reservationEnabled" /> {{ t('catalog.flags.reservationEnabled') }}</label>
            <label><p-checkbox [binary]="true" [(ngModel)]="form.rentalEnabled" /> {{ t('catalog.flags.rentalEnabled') }}</label>
            <label><p-checkbox [binary]="true" [(ngModel)]="form.assetEnabled" /> {{ t('catalog.flags.assetEnabled') }}</label>
            <label><p-checkbox [binary]="true" [(ngModel)]="form.maintenanceEnabled" /> {{ t('catalog.flags.maintenanceEnabled') }}</label>
            <label><p-checkbox [binary]="true" [(ngModel)]="form.digitalDeliveryEnabled" /> {{ t('catalog.flags.digitalDeliveryEnabled') }}</label>
            <label><p-checkbox [binary]="true" [(ngModel)]="form.nonRequestable" /> {{ t('catalog.flags.nonRequestable') }}</label>
            <label><p-checkbox [binary]="true" [(ngModel)]="form.nonOrderable" /> {{ t('catalog.flags.nonOrderable') }}</label>
            @if (form.nonRequestable || form.nonOrderable) {
              <p-tag severity="info" [value]="t('catalog.flags.restrictionBadge')" />
            }
          </section>
        }

        @if (activeTab() === 'inventory' && offering(); as currentOffering) {
          <ain-offering-inventory-workspace
            [offeringId]="currentOffering.id"
            [initialSubTab]="inventorySubTab()"
            [balances]="offeringBalances()"
            [ledger]="offeringLedger()"
            [warehouses]="warehouses()"
            [showMovements]="canViewInventoryMovements()"
            [showLots]="canViewInventoryMovements()"
            [showCosting]="canViewInventoryCosting()"
            [showReservations]="canViewInventoryReservations()"
            (inventoryChanged)="onOfferingInventoryChanged()" />
        }

        @if (activeTab() === 'storefront' && offering(); as currentOffering) {
          <ain-offering-storefront-publishing-panel [offeringId]="currentOffering.id" />
        }

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
    .header-card h3 { margin: 0; }
    .header-card p { margin: 6px 0 0; color: #64748b; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .tabs { display: flex; gap: 8px; flex-wrap: wrap; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-top: 10px; }
    .tabs button { border: 1px solid #cbd5e1; border-radius: 999px; background: white; padding: 6px 12px; cursor: pointer; }
    .tabs button.active { border-color: #1d4ed8; color: #1d4ed8; font-weight: 700; }
    .tabs button:disabled { opacity: .45; cursor: not-allowed; }
    .general-subtabs { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
    .general-subtabs button { border: 1px solid #cbd5e1; border-radius: 8px; background: #f8fafc; padding: 6px 12px; cursor: pointer; font-size: 0.875rem; }
    .general-subtabs button.active { border-color: #0f766e; color: #0f766e; background: #ecfdf5; font-weight: 700; }
    .section-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-top: 10px; background: #fff; }
    .section-header { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
    .section-header h4 { margin: 0; font-size: 0.95rem; color: #0f172a; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; margin-top: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid label.full, .form-grid .full { grid-column: 1 / -1; }
    .form-grid span { color: #475569; font-size: 12px; font-weight: 600; }
    .radio-wrapper { display: grid; gap: 6px; }
    .radio-group { display: flex; align-items: center; flex-wrap: wrap; gap: 14px; min-height: 2.5rem; }
    .radio-item { display: inline-flex; align-items: center; gap: 8px; }
    .editor-wrapper { display: grid; gap: 6px; }
    .profile-actions { display: flex; align-items: end; gap: 8px; }
    .profile-errors { background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 8px 10px; color: #991b1b; }
    .profile-errors p { margin: 0; }
    .flags-form { display: grid; gap: 10px; margin-top: 10px; }
    .flags-form label { display: flex; gap: 8px; align-items: center; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class CatalogOfferingDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly currentContext = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly activeTab = signal<OfferingTab>('general');
  readonly inventorySubTab = signal<OfferingInventorySubTab>('balances');
  readonly generalSection = signal<OfferingGeneralSection>('core');
  readonly isCreateMode = signal(false);
  readonly offering = signal<CatalogOffering | null>(null);
  readonly allBalances = signal<InventoryStockBalance[]>([]);
  readonly allLedger = signal<InventoryLedgerEntry[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly profileValidationErrors = signal<string[]>([]);
  readonly fieldErrors = new FormFieldErrorsState();
  readonly offeringBalances = computed(() => this.allBalances().filter(item => item.offeringId === this.offering()?.id));
  readonly offeringLedger = computed(() => this.allLedger().filter(item => item.offeringId === this.offering()?.id));
  selectedProfileType = 'PhysicalGoodProfile';
  profilePayloadJson = '{}';

  readonly form = {
    type: 'PhysicalProduct',
    code: '',
    title: '',
    slug: '',
    status: 'Draft',
    visibility: 'Public',
    shortDescription: '',
    fullDescription: '',
    basePrice: 0,
    currency: 'IRR',
    pricingModel: 'Fixed',
    fulfillmentModel: 'Online',
    metaTitle: '',
    metaDescription: '',
    salesEnabled: true,
    purchaseEnabled: true,
    inventoryEnabled: true,
    reservationEnabled: false,
    rentalEnabled: false,
    assetEnabled: false,
    maintenanceEnabled: false,
    digitalDeliveryEnabled: false,
    categoryId: null as string | null,
    consumptionClassification: 'Unspecified',
    nonRequestable: false,
    nonOrderable: false
  };

  readonly typeOptions = [
    { label: 'Physical Product', value: 'PhysicalProduct' },
    { label: 'Digital Product', value: 'DigitalProduct' },
    { label: 'Service', value: 'Service' },
    { label: 'Course', value: 'Course' },
    { label: 'Appointment', value: 'Appointment' },
    { label: 'Rental', value: 'Rental' },
    { label: 'Hotel Room', value: 'HotelRoom' },
    { label: 'Restaurant Table', value: 'RestaurantTable' },
    { label: 'Vehicle', value: 'Vehicle' },
    { label: 'Real Estate', value: 'RealEstate' },
    { label: 'Auction', value: 'Auction' },
    { label: 'Tender', value: 'Tender' },
    { label: 'Bundle', value: 'Bundle' },
    { label: 'Subscription', value: 'Subscription' }
  ];

  readonly statusOptions = [
    { label: 'Draft', value: 'Draft' },
    { label: 'Pending Approval', value: 'PendingApproval' },
    { label: 'Published', value: 'Published' },
    { label: 'Unpublished', value: 'Unpublished' },
    { label: 'Archived', value: 'Archived' }
  ];

  readonly pricingModelOptions = [
    { label: 'Fixed', value: 'Fixed' },
    { label: 'Tiered', value: 'Tiered' },
    { label: 'Usage-Based', value: 'UsageBased' },
    { label: 'Subscription', value: 'Subscription' }
  ];

  readonly fulfillmentModelOptions = [
    { label: 'Online', value: 'Online' },
    { label: 'Physical Delivery', value: 'PhysicalDelivery' },
    { label: 'On Site', value: 'OnSite' },
    { label: 'Hybrid', value: 'Hybrid' }
  ];

  readonly profileTypeOptions = offeringProfileTypeOptions();

  consumptionClassificationOptions(): Array<{ label: string; value: string }> {
    return [
      { label: this.t('catalog.categories.classification.unspecified'), value: 'Unspecified' },
      { label: this.t('catalog.categories.classification.consumable'), value: 'Consumable' },
      { label: this.t('catalog.categories.classification.nonConsumable'), value: 'NonConsumable' }
    ];
  }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const tab = params.get('tab');
      const inventoryTab = params.get('inventoryTab');
      if (tab === 'balances') {
        this.activeTab.set('inventory');
        this.inventorySubTab.set('balances');
        return;
      }
      if (tab === 'ledger') {
        this.activeTab.set('inventory');
        this.inventorySubTab.set('ledger');
        return;
      }
      if (tab === 'general' || tab === 'attributes' || tab === 'media' || tab === 'profiles' || tab === 'flags' || tab === 'inventory' || tab === 'storefront') {
        this.activeTab.set(tab);
      }
      if (inventoryTab && this.isInventorySubTab(inventoryTab)) {
        this.inventorySubTab.set(inventoryTab);
      }
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.loadFromRoute(params.get('id'));
    });
  }

  onOfferingInventoryChanged(): void {
    const id = this.offering()?.id;
    if (!id) {
      return;
    }
    this.loadInventoryData();
  }

  setTab(tab: OfferingTab): void {
    this.activeTab.set(tab);
    const queryParams: Record<string, string | null> = { tab };
    if (tab === 'inventory') {
      queryParams['inventoryTab'] = this.inventorySubTab();
    } else {
      queryParams['inventoryTab'] = null;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge'
    });
  }

  isInventorySubTab(value: string): value is OfferingInventorySubTab {
    return ['balances', 'ledger', 'movements', 'lots', 'costing', 'reservations'].includes(value);
  }

  canViewInventoryTab(): boolean {
    return this.hasPermission('inventory.stock.view');
  }

  canViewInventoryMovements(): boolean {
    return this.hasPermission('inventory.stock.view');
  }

  canViewInventoryCosting(): boolean {
    return this.hasPermission('inventory.costing.view');
  }

  canViewInventoryReservations(): boolean {
    return this.hasPermission('inventory.reservations.create') || this.hasPermission('inventory.tasks.execute');
  }

  canViewStorefrontTab(): boolean {
    const context = this.currentContext.context();
    const entitlements = new Set(context?.entitlements ?? []);
    return entitlements.has('catalog') || this.hasPermission('marketplace.storefront.view');
  }

  private hasPermission(key: string): boolean {
    const context = this.currentContext.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }
    const permissions = new Set((context?.permissions ?? []).map(item => item.key));
    return permissions.has(key);
  }

  private loadFromRoute(id: string | null): void {
    this.error.set('');
    if (!id) {
      this.loading.set(false);
      this.error.set(this.t('catalog.offerings.notFound'));
      return;
    }

    if (id === 'new') {
      this.isCreateMode.set(true);
      this.offering.set(null);
      this.generalSection.set('core');
      this.loading.set(false);
      return;
    }

    this.isCreateMode.set(false);
    this.load(id);
  }

  save(): void {
    this.error.set('');
    const valid = this.fieldErrors.validate([
      { field: 'code', value: () => this.form.code, messageKey: 'common.validation.required' },
      { field: 'title', value: () => this.form.title, messageKey: 'common.validation.required' }
    ]);
    if (!valid) {
      this.activeTab.set('general');
      this.generalSection.set('core');
      return;
    }

    this.ensureSlugBeforeSave();
    const payload = this.toPayload();
    if (this.isCreateMode()) {
      this.service.createOffering(payload).subscribe({
        next: created => {
          const desiredStatus = this.form.status.trim();
          if (desiredStatus && desiredStatus.toLowerCase() !== 'draft') {
            this.service.updateOfferingStatus(created.id, desiredStatus).subscribe({
              next: () => void this.router.navigate(['/catalog/offerings', created.id]),
              error: () => this.error.set(this.t('catalog.offerings.updateFailed'))
            });
            return;
          }
          void this.router.navigate(['/catalog/offerings', created.id]);
        },
        error: () => this.error.set(this.t('catalog.offerings.createFailed'))
      });
      return;
    }

    const id = this.offering()?.id;
    if (!id) return;
    const desiredStatus = this.form.status.trim();
    this.service.updateOffering(id, payload).subscribe({
      next: updated => {
        const applyUpdateLocally = () => {
          this.offering.set(updated);
          this.syncForm(updated);
        };

        if (desiredStatus && desiredStatus.toLowerCase() !== updated.status.toLowerCase()) {
          this.service.updateOfferingStatus(id, desiredStatus).subscribe({
            next: () => this.load(id),
            error: () => this.error.set(this.t('catalog.offerings.updateFailed'))
          });
          return;
        }

        applyUpdateLocally();
      },
      error: () => this.error.set(this.t('catalog.offerings.updateFailed'))
    });
  }

  publish(): void {
    const current = this.offering();
    if (!current) return;
    this.service.publishOffering(current.id).subscribe({
      next: () => this.load(current.id),
      error: () => this.error.set(this.t('catalog.offerings.updateFailed'))
    });
  }

  unpublish(): void {
    const current = this.offering();
    if (!current) return;
    this.service.unpublishOffering(current.id).subscribe({
      next: () => this.load(current.id),
      error: () => this.error.set(this.t('catalog.offerings.updateFailed'))
    });
  }

  onMediaChanged(media: CatalogOffering['media']): void {
    const current = this.offering();
    if (!current) {
      return;
    }

    const primaryImageUrl = media?.find(item => item.isPrimary)?.imageUrl ?? media?.[0]?.imageUrl ?? null;
    this.offering.set({
      ...current,
      media: media ?? [],
      primaryImageUrl
    });
  }

  onProfileTypeChange(profileType: string): void {
    this.selectedProfileType = profileType;
    this.profileValidationErrors.set([]);
    this.profilePayloadJson = serializeProfilePayload(defaultProfileModel(profileType));
    this.loadProfile();
  }

  loadProfile(): void {
    const id = this.offering()?.id;
    if (!id) return;
    this.profileValidationErrors.set([]);
    this.service.getOfferingProfile(id, this.selectedProfileType).subscribe({
      next: profile => {
        this.profilePayloadJson =
          profile.payloadJson?.trim() ||
          serializeProfilePayload(defaultProfileModel(this.selectedProfileType));
      },
      error: () => {
        this.profilePayloadJson = serializeProfilePayload(defaultProfileModel(this.selectedProfileType));
      }
    });
  }

  validateProfile(): void {
    const id = this.offering()?.id;
    if (!id) return;
    this.service.validateOfferingProfile(id, this.selectedProfileType, this.profilePayloadJson).subscribe({
      next: result => {
        this.profileValidationErrors.set(result.errors ?? []);
      },
      error: () => this.error.set(this.t('catalog.profiles.validateFailed'))
    });
  }

  saveProfile(): void {
    const id = this.offering()?.id;
    if (!id) return;
    this.error.set('');
    this.service.upsertOfferingProfile(id, this.selectedProfileType, this.profilePayloadJson).subscribe({
      next: () => {
        this.profileValidationErrors.set([]);
      },
      error: () => this.error.set(this.t('catalog.profiles.saveFailed'))
    });
  }

  private load(id: string): void {
    this.loading.set(true);
    this.error.set('');
    this.service.getOffering(id).subscribe({
      next: offering => {
        this.offering.set(offering);
        this.syncForm(offering);
        this.selectedProfileType = this.defaultProfileTypeForOffering(offering.type);
        this.loadProfile();
        this.loadInventoryData();
      },
      error: () => {
        this.loading.set(false);
        this.error.set(this.t('catalog.offerings.loadFailed'));
      }
    });
  }

  private loadInventoryData(): void {
    this.service.listStockBalances().subscribe({
      next: balances => {
        this.allBalances.set(balances);
        this.service.listLedger().subscribe({
          next: ledger => {
            this.allLedger.set(ledger);
            this.service.listWarehouses().subscribe({
              next: warehouses => {
                this.warehouses.set(warehouses);
                this.loading.set(false);
              },
              error: () => {
                this.warehouses.set([]);
                this.loading.set(false);
              }
            });
          },
          error: () => {
            this.loading.set(false);
            this.error.set(this.t('catalog.offerings.inventoryLoadFailed'));
          }
        });
      },
      error: () => {
        this.loading.set(false);
        this.error.set(this.t('catalog.offerings.inventoryLoadFailed'));
      }
    });
  }

  resolveWarehouseLabel(warehouseId: string | null | undefined): string {
    if (!warehouseId) {
      return '-';
    }

    const warehouse = this.warehouses().find(item => item.id === warehouseId);
    return warehouse ? `${warehouse.code} - ${warehouse.name}` : warehouseId;
  }

  private syncForm(offering: CatalogOffering): void {
    this.form.type = offering.type;
    this.form.code = offering.code;
    this.form.title = offering.title;
    this.form.slug = offering.slug;
    this.form.status = offering.status;
    this.form.visibility = offering.visibility;
    this.form.shortDescription = offering.shortDescription;
    this.form.fullDescription = offering.fullDescription;
    this.form.basePrice = offering.basePrice;
    this.form.currency = offering.currency;
    this.form.pricingModel = offering.pricingModel;
    this.form.fulfillmentModel = offering.fulfillmentModel;
    this.form.metaTitle = offering.metaTitle;
    this.form.metaDescription = offering.metaDescription;
    this.form.salesEnabled = offering.salesEnabled;
    this.form.purchaseEnabled = offering.purchaseEnabled;
    this.form.inventoryEnabled = offering.inventoryEnabled;
    this.form.reservationEnabled = offering.reservationEnabled;
    this.form.rentalEnabled = offering.rentalEnabled;
    this.form.assetEnabled = offering.assetEnabled;
    this.form.maintenanceEnabled = offering.maintenanceEnabled;
    this.form.digitalDeliveryEnabled = offering.digitalDeliveryEnabled;
    this.form.categoryId = offering.categoryId ?? null;
    this.form.consumptionClassification = offering.consumptionClassification ?? 'Unspecified';
    this.form.nonRequestable = offering.nonRequestable ?? false;
    this.form.nonOrderable = offering.nonOrderable ?? false;
  }

  private ensureSlugBeforeSave(): void {
    if (this.form.slug.trim()) {
      return;
    }

    const fromTitle = this.slugify(this.form.title);
    this.form.slug = fromTitle || this.form.code.trim().toLowerCase();
  }

  private slugify(value: string): string {
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\u0600-\u06ff-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return normalized;
  }

  private toPayload(): CreateOfferingPayload {
    return {
      type: this.form.type,
      code: this.form.code.trim(),
      title: this.form.title.trim(),
      slug: this.form.slug.trim(),
      shortDescription: this.form.shortDescription.trim(),
      fullDescription: this.form.fullDescription.trim(),
      basePrice: Number(this.form.basePrice || 0),
      currency: this.form.currency.trim() || 'IRR',
      pricingModel: this.form.pricingModel.trim() || 'Fixed',
      fulfillmentModel: this.form.fulfillmentModel.trim() || 'Online',
      salesEnabled: this.form.salesEnabled,
      purchaseEnabled: this.form.purchaseEnabled,
      inventoryEnabled: this.form.inventoryEnabled,
      reservationEnabled: this.form.reservationEnabled,
      rentalEnabled: this.form.rentalEnabled,
      assetEnabled: this.form.assetEnabled,
      maintenanceEnabled: this.form.maintenanceEnabled,
      digitalDeliveryEnabled: this.form.digitalDeliveryEnabled,
      visibility: this.form.visibility,
      metaTitle: this.form.metaTitle.trim() || this.form.title.trim(),
      metaDescription: this.form.metaDescription.trim(),
      categoryId: this.form.categoryId,
      consumptionClassification: this.form.consumptionClassification,
      nonRequestable: this.form.nonRequestable,
      nonOrderable: this.form.nonOrderable
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private defaultProfileTypeForOffering(offeringType: string): string {
    const key = offeringType.toLowerCase();
    if (key.includes('digital')) return 'DigitalProfile';
    if (key.includes('service') || key.includes('course') || key.includes('appointment')) return 'ServiceProfile';
    if (key.includes('rental')) return 'RentalProfile';
    if (key.includes('hotel') || key.includes('restaurant') || key.includes('hospitality')) return 'HospitalityProfile';
    if (key.includes('subscription')) return 'SubscriptionProfile';
    if (key.includes('asset') || key.includes('vehicle') || key.includes('realestate')) return 'AssetProfile';
    return 'PhysicalGoodProfile';
  }
}
